/**
 * Turn a raw user-agent into a short device label for the sessions list
 * (PB-2927).
 *
 * Deliberately crude, and deliberately on this side of the wire. The auth
 * service sends the string unparsed so that no user-agent parsing dependency
 * has to live in a security-sensitive service, and this is presentation: it
 * only has to help an administrator recognise "that's my laptop". It is never
 * used for a decision, so a miss costs a vaguer label and nothing else.
 *
 * Order matters. Every Chromium browser still says "Safari" in its UA, and Edge
 * says "Chrome" as well, so the most specific token has to win first.
 */

const BROWSERS: ReadonlyArray<readonly [pattern: RegExp, name: string]> = [
  [/\bEdg[A-Z]?\//, "Edge"],
  [/\bOPR\/|\bOpera\//, "Opera"],
  [/\bFirefox\//, "Firefox"],
  [/\bChrome\//, "Chrome"],
  [/\bSafari\//, "Safari"],
];

const PLATFORMS: ReadonlyArray<readonly [pattern: RegExp, name: string]> = [
  [/\biPhone\b/, "iPhone"],
  [/\biPad\b/, "iPad"],
  [/\bAndroid\b/, "Android"],
  [/\bMac OS X\b|\bMacintosh\b/, "macOS"],
  [/\bWindows\b/, "Windows"],
  [/\bCrOS\b/, "ChromeOS"],
  [/\bLinux\b/, "Linux"],
];

function firstMatch(
  table: ReadonlyArray<readonly [RegExp, string]>,
  userAgent: string,
): string | null {
  for (const [pattern, name] of table) {
    if (pattern.test(userAgent)) return name;
  }
  return null;
}

/**
 * `"Chrome on macOS"`, or the best available half, or `"Unknown device"`.
 *
 * Never returns the raw string: an unrecognised UA is usually a bot or a
 * scripted client, and pasting 200 opaque characters into a table cell helps
 * nobody. Callers that want the original should keep it themselves.
 */
export function deviceLabel(userAgent: string | null): string {
  const value = userAgent?.trim();
  if (!value) return "Unknown device";

  const browser = firstMatch(BROWSERS, value);
  const platform = firstMatch(PLATFORMS, value);

  if (browser && platform) return `${browser} on ${platform}`;
  return browser ?? platform ?? "Unknown device";
}
